
import React, { createContext, useState, useContext, useEffect } from 'react';

type ColorScheme = 'light' | 'dark' | 'system';
type Density = 'compact' | 'normal' | 'comfortable';
type AccentColor = 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'yellow';

interface ThemeContextType {
  colorScheme: ColorScheme;
  density: Density;
  compactSidebar: boolean;
  animations: boolean;
  accentColor: AccentColor;
  updateColorScheme: (scheme: ColorScheme) => void;
  updateDensity: (density: Density) => void;
  updateCompactSidebar: (compact: boolean) => void;
  updateAnimations: (enabled: boolean) => void;
  updateAccentColor: (color: AccentColor) => void;
}

const defaultTheme: ThemeContextType = {
  colorScheme: 'light',
  density: 'normal',
  compactSidebar: false,
  animations: true,
  accentColor: 'purple',
  updateColorScheme: () => {},
  updateDensity: () => {},
  updateCompactSidebar: () => {},
  updateAnimations: () => {},
  updateAccentColor: () => {},
};

const ThemeContext = createContext<ThemeContextType>(defaultTheme);

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [colorScheme, setColorScheme] = useState<ColorScheme>('light');
  const [density, setDensity] = useState<Density>('normal');
  const [compactSidebar, setCompactSidebar] = useState<boolean>(false);
  const [animations, setAnimations] = useState<boolean>(true);
  const [accentColor, setAccentColor] = useState<AccentColor>('purple');

  // Load saved preferences on startup
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-preferences');
    if (savedTheme) {
      try {
        const preferences = JSON.parse(savedTheme);
        setColorScheme(preferences.colorScheme || 'light');
        setDensity(preferences.density || 'normal');
        setCompactSidebar(preferences.compactSidebar || false);
        setAnimations(preferences.animations !== undefined ? preferences.animations : true);
        // Migrate legacy fluorescent-green default to brand purple
        const savedAccent = preferences.accentColor;
        setAccentColor(savedAccent && savedAccent !== 'green' ? savedAccent : 'purple');
      } catch (error) {
        console.error("Error loading theme preferences:", error);
      }
    }
  }, []);

  // Apply theme whenever any theme property changes
  useEffect(() => {
    applyTheme(colorScheme, accentColor, animations, density);
  }, [colorScheme, accentColor, animations, density]);

  // Function to apply theme
  const applyTheme = (scheme: ColorScheme, accent: AccentColor, enableAnimations: boolean, densityValue: Density) => {
    const root = document.documentElement;
    
    // Apply color scheme
    if (scheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.remove('light', 'dark');
      root.classList.add(systemTheme);
    } else {
      root.classList.remove('light', 'dark');
      root.classList.add(scheme);
    }

    // Apply accent color by updating CSS variables
    const accentColorValue = getAccentColorValue(accent);
    root.style.setProperty('--primary', accentColorValue);
    root.style.setProperty('--accent', accentColorValue);
    root.style.setProperty('--ring', accentColorValue);
    
    // Apply animations
    if (!enableAnimations) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Apply density
    root.setAttribute('data-density', densityValue);
    
    console.log('Theme applied:', { scheme, accent, enableAnimations, densityValue });
    console.log('Accent color value set:', accentColorValue);
    console.log('Primary CSS variable:', root.style.getPropertyValue('--primary'));
  };

  const getAccentColorValue = (color: AccentColor): string => {
    switch (color) {
      case 'blue': return '220 98% 61%';
      case 'purple': return '262 83% 58%';
      case 'green': return '160 84% 39%';
      case 'red': return '346 77% 49%';
      case 'orange': return '25 95% 53%';
      case 'yellow': return '48 96% 53%';
      default: return '262 83% 58%';
    }
  };

  const saveThemePreferences = (updates: Partial<Omit<ThemeContextType, 'updateColorScheme' | 'updateDensity' | 'updateCompactSidebar' | 'updateAnimations' | 'updateAccentColor'>>) => {
    const currentPreferences = localStorage.getItem('theme-preferences');
    let preferences = {};
    
    if (currentPreferences) {
      try {
        preferences = JSON.parse(currentPreferences);
      } catch (error) {
        console.error("Error parsing saved preferences:", error);
      }
    }
    
    const updatedPreferences = { 
      ...preferences, 
      ...updates 
    };
    
    localStorage.setItem('theme-preferences', JSON.stringify(updatedPreferences));
  };

  const updateColorScheme = (scheme: ColorScheme) => {
    setColorScheme(scheme);
    saveThemePreferences({ colorScheme: scheme });
  };

  const updateDensity = (newDensity: Density) => {
    setDensity(newDensity);
    saveThemePreferences({ density: newDensity });
  };

  const updateCompactSidebar = (compact: boolean) => {
    console.log('updateCompactSidebar called with:', compact);
    setCompactSidebar(compact);
    saveThemePreferences({ compactSidebar: compact });
  };

  const updateAnimations = (enabled: boolean) => {
    setAnimations(enabled);
    saveThemePreferences({ animations: enabled });
  };

  const updateAccentColor = (color: AccentColor) => {
    setAccentColor(color);
    saveThemePreferences({ accentColor: color });
  };

  return (
    <ThemeContext.Provider 
      value={{
        colorScheme,
        density,
        compactSidebar,
        animations,
        accentColor,
        updateColorScheme,
        updateDensity,
        updateCompactSidebar,
        updateAnimations,
        updateAccentColor
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
